import { a as joinUrl, i as buildPath, n as metaExamples, r as INPUT_KINDS } from "./response-examples-2JSA2FBV.js";
import { c as TERMS, d as PERMISSIONS, t as ClaimsSchema, u as TERM_VERSIONS } from "./claims.schemas-ArmZYY7U.js";
import * as z from "zod";
import { deepFreeze, err, ok } from "@spark/domain";

//#region src/contract/contract.ts
/**
* Create an empty contract.
*/
function contract(meta = {}) {
	return makeContract({
		...meta,
		routers: {}
	});
}
function makeContract(def) {
	const helpers = Object.fromEntries(Object.entries(def.routers).map(([id, router]) => [id, makeRouterHelpers(router)]));
	return Object.freeze({
		...helpers,
		def,
		add: (id, router) => {
			if (id in def.routers || id === "def" || id === "add") throw new Error(`Duplicate or reserved router id "${id}"`);
			return makeContract({
				...def,
				routers: {
					...def.routers,
					[id]: router
				}
			});
		}
	});
}
function makeRouterHelpers(router) {
	return {
		...Object.fromEntries(Object.entries(router.def.routes).map(([id, route]) => [id, makeRouteHelpers(router.def.prefix + route.def.path, route)])),
		Router: () => router
	};
}
function makeRouteHelpers(template, route) {
	return {
		Route: () => route,
		Path: (params) => buildPath(template, params),
		Url: (...args) => {
			const base = args.at(-1);
			const params = args.length > 1 ? args[0] : void 0;
			return joinUrl(base, buildPath(template, params));
		},
		Schema: (kind) => {
			const schema = route.def.inputs[kind];
			if (!schema) throw new Error(`Route does not declare a "${kind}" input`);
			return schema;
		}
	};
}

//#endregion
//#region src/contract/pagination.ts
const LIMIT_MAX = 1e3;
const LIMIT_DEFAULT = 100;
/**
* Resolve {@link PaginationOptions} against the defaults (cap 1000, default 100).
*/
function paginationDef(kind, options = {}) {
	const max = options.limit?.max ?? LIMIT_MAX;
	const dflt = options.limit?.default ?? LIMIT_DEFAULT;
	if (max < 1 || dflt < 1 || dflt > max) throw new Error(`Invalid pagination limit: default ${dflt} must be between 1 and max ${max}`);
	return {
		kind,
		limit: {
			max,
			default: dflt
		}
	};
}
function limitSchema(limit) {
	return z.coerce.number().int().min(1).max(limit.max).default(limit.default);
}
function offsetQueryShape(limit) {
	return {
		limit: limitSchema(limit),
		offset: z.coerce.number().int().min(0).default(0)
	};
}
function cursorQueryShape(limit) {
	return {
		limit: limitSchema(limit),
		cursor: z.string().optional()
	};
}
const OffsetPaginationMetaSchema = z.object({
	limit: z.int().min(0),
	offset: z.int().min(0),
	total: z.int().min(0)
});
const CursorPaginationMetaSchema = z.object({
	limit: z.int().min(0),
	nextCursor: z.string().nullable()
});
function queryShape(def) {
	return def.kind === "offset" ? offsetQueryShape(def.limit) : cursorQueryShape(def.limit);
}
function metaSchema(def) {
	return def.kind === "offset" ? OffsetPaginationMetaSchema : CursorPaginationMetaSchema;
}
function asObject(schema, context) {
	if (!(schema instanceof z.ZodObject)) throw new Error(`Pagination requires the ${context} schema to be a z.object`);
	return schema;
}
function assertNoOverlap(schema, added, context) {
	const duplicates = added.filter((key) => key in schema.shape);
	if (duplicates.length > 0) throw new Error(`Pagination fields already declared on the ${context} schema: ${duplicates.join(", ")}`);
}
/**
* Merge the pagination query params of a declaration into a query schema
* (or create the query schema when the route declares none). The schema must
* be a `z.object` without `limit`/`offset`/`cursor` fields of its own.
*/
function mergePaginationQuery(schema, def) {
	const shape = queryShape(def);
	if (schema === void 0) return z.object(shape);
	const base = asObject(schema, "query");
	assertNoOverlap(base, Object.keys(shape), "query");
	return base.extend(shape);
}
function unwrap(schema) {
	let current = schema;
	while (current instanceof z.ZodOptional || current instanceof z.ZodNullable || current instanceof z.ZodDefault || current instanceof z.ZodReadonly) current = current.unwrap();
	return current;
}
/**
* The single array field of a paginated response schema — the field holding
* the page's items. Throws unless there is exactly one array field.
*/
function paginatedArrayField(schema) {
	const fields = Object.entries(schema.shape).filter(([, field]) => unwrap(field) instanceof z.ZodArray).map(([key]) => key);
	if (fields.length !== 1) throw new Error(`Paginated response schemas must declare exactly one array field, found ${fields.length === 0 ? "none" : fields.join(", ")}`);
	return fields[0];
}
function examplePagination(def, example, field) {
	const items = example[field];
	return def.kind === "offset" ? {
		limit: def.limit.default,
		offset: 0,
		total: Array.isArray(items) ? items.length : 0
	} : {
		limit: def.limit.default,
		nextCursor: null
	};
}
/**
* Merge the `pagination` object of a declaration into a 2xx response schema.
* The schema must be a `z.object` — or a pipe (e.g. `z.preprocess`) into one —
* with exactly one array field (the page's items) and no `pagination` field of
* its own; declared meta examples are carried over with a matching
* `pagination` value added.
*/
function mergePaginationOutput(schema, def) {
	const pipe = schema instanceof z.ZodPipe ? schema : void 0;
	const base = asObject(pipe ? pipe.out : schema, "2xx response");
	assertNoOverlap(base, ["pagination"], "2xx response");
	const field = paginatedArrayField(base);
	const extendedObject = base.extend({ pagination: metaSchema(def) });
	const extended = pipe ? pipe.in.pipe(extendedObject) : extendedObject;
	const meta = schema.meta();
	if (meta === void 0) return extended;
	const examples = metaExamples(schema).map((example) => example && typeof example === "object" ? {
		...example,
		pagination: examplePagination(def, example, field)
	} : example);
	return extended.meta({
		...meta,
		...examples.length > 0 ? { examples } : {}
	});
}

//#endregion
//#region src/contract/route.ts
/**
* Create a route from a method and `{param}` path template.
*/
function route(init) {
	return makeRoute({
		method: init.method,
		path: init.path,
		meta: {},
		inputs: {},
		outputs: {},
		session: false,
		bearer: false,
		features: [],
		pagination: false
	});
}
const is2xx = (status) => status >= 200 && status < 300;
function makeRoute(def) {
	return Object.freeze({
		def,
		meta: (meta) => makeRoute({
			...def,
			meta: {
				...def.meta,
				...meta
			}
		}),
		input: (kind, schema) => makeRoute({
			...def,
			inputs: {
				...def.inputs,
				[kind]: kind === "query" && def.pagination !== false ? mergePaginationQuery(schema, def.pagination) : schema
			}
		}),
		output: (status, schema) => {
			if (status in def.outputs) throw new Error(`Duplicate output status ${status}`);
			return makeRoute({
				...def,
				outputs: {
					...def.outputs,
					[status]: def.pagination !== false && is2xx(status) ? mergePaginationOutput(schema, def.pagination) : schema
				}
			});
		},
		pagination: (kind, options) => {
			if (def.pagination !== false) throw new Error("Pagination already declared");
			const pagination = paginationDef(kind, options);
			return makeRoute({
				...def,
				pagination,
				inputs: {
					...def.inputs,
					query: mergePaginationQuery(def.inputs.query, pagination)
				},
				outputs: Object.fromEntries(Object.entries(def.outputs).map(([status, schema]) => [status, is2xx(Number(status)) ? mergePaginationOutput(schema, pagination) : schema]))
			});
		},
		session: (kind) => makeRoute({
			...def,
			session: kind === void 0 ? true : typeof kind === "string" ? [kind] : [...kind]
		}),
		bearer: (options) => makeRoute({
			...def,
			bearer: options?.prefix ?? true
		}),
		feature: (feature) => makeRoute({
			...def,
			features: def.features.includes(feature) ? def.features : [...def.features, feature]
		})
	});
}

//#endregion
//#region src/contract/router.ts
/**
* Create an empty router.
*/
function router() {
	return makeRouter({
		routes: {},
		meta: {},
		prefix: "",
		session: false,
		bearer: false,
		features: []
	});
}
/**
* Apply the router-level requirements to a route: session/bearer only when the
* route has none of its own, features always (cumulative, deduplicated by the
* route builder). Returns the route unchanged when nothing applies.
*/
function applyRequirements(route, def) {
	let result = route;
	if (def.session !== false && result.def.session === false) result = def.session === true ? result.session() : result.session(def.session);
	if (def.bearer !== false && result.def.bearer === false) result = def.bearer === true ? result.bearer() : result.bearer({ prefix: def.bearer });
	for (const feature of def.features) result = result.feature(feature);
	return result;
}
function mapRoutes(routes, transform) {
	return Object.fromEntries(Object.entries(routes).map(([id, route]) => [id, transform(route)]));
}
function makeRouter(def) {
	return Object.freeze({
		def,
		meta: (meta) => makeRouter({
			...def,
			meta: {
				...def.meta,
				...meta
			}
		}),
		add: (id, route) => {
			if (id in def.routes) throw new Error(`Duplicate route id "${id}"`);
			const signature = `${route.def.method} ${route.def.path}`;
			for (const existing of Object.values(def.routes)) if (`${existing.def.method} ${existing.def.path}` === signature) throw new Error(`Duplicate route "${signature}"`);
			return makeRouter({
				...def,
				routes: {
					...def.routes,
					[id]: applyRequirements(route, def)
				}
			});
		},
		prefix: (prefix) => makeRouter({
			...def,
			prefix
		}),
		session: (kind) => {
			const session = kind === void 0 ? true : typeof kind === "string" ? [kind] : [...kind];
			return makeRouter({
				...def,
				session,
				routes: mapRoutes(def.routes, (route) => applyRequirements(route, {
					...def,
					session,
					features: []
				}))
			});
		},
		bearer: (options) => {
			const bearer = options?.prefix ?? true;
			return makeRouter({
				...def,
				bearer,
				routes: mapRoutes(def.routes, (route) => applyRequirements(route, {
					...def,
					bearer,
					features: []
				}))
			});
		},
		feature: (feature) => makeRouter({
			...def,
			features: def.features.includes(feature) ? def.features : [...def.features, feature],
			routes: mapRoutes(def.routes, (route) => route.feature(feature))
		})
	});
}

//#endregion
//#region src/contract/client.ts
/**
* Thrown when a response has an undeclared status code or a body that fails schema validation.
*/
var ContractResponseError = class extends Error {
	status;
	body;
	constructor(message, status, body, options) {
		super(message, options);
		this.name = "ContractResponseError";
		this.status = status;
		this.body = body;
	}
};
/**
* Fetch client for a contract. Callable with or without `new`.
*
* Each route accepts its inputs as `{ query?, path?, headers?, body? }`; when a route declares
* exactly one input kind, the bare input is also accepted (e.g. `client.answers.get({ id })`).
* Edge case: a bare body whose own keys are all input-kind names is read as the wrapped form —
* use the wrapped form there.
*
* Every route also accepts {@link ContractCallOptions} as its last argument to adjust the
* actual request, e.g. per-call headers merged over the client-level headers
* (`client.session.me(undefined, { headers: { authorization: token } })`).
*
* Responses are returned as `{ status, body }` with the body validated against the output schema
* for the returned status. An undeclared status code or a body that fails validation throws a
* {@link ContractResponseError}; a declared status (e.g. an explicit 400 output) is returned normally.
*
* Paginated routes additionally expose `.all(input?, options?)`: an async generator that walks
* every page (following offsets or cursors from the response's `pagination` object) and yields
* the items of the paginated array field as they arrive
* (`for await (const theme of client.themes.list.all()) { ... }`). A declared non-2xx response
* aborts the walk with a {@link ContractResponseError}.
*/
const ContractClient = contractClient;
function contractClient(contract, options) {
	return Object.fromEntries(Object.entries(contract.def.routers).map(([routerId, router]) => [routerId, Object.fromEntries(Object.entries(router.def.routes).map(([routeId, route]) => {
		const template = router.def.prefix + route.def.path;
		const fn = (input, callOptions) => call(template, route, options, input, callOptions);
		return [routeId, route.def.pagination === false ? fn : Object.assign(fn, { all: (input, callOptions) => paginateAll(template, route, options, input, callOptions) })];
	}))]));
}
async function call(template, route, options, input, callOptions) {
	const { status, body } = await perform(template, route, options, input, callOptions);
	return {
		status,
		body
	};
}
async function perform(template, route, options, input, callOptions) {
	const { query, path, headers, body } = resolveInput(route, input);
	const url = joinUrl(options.baseUrl, buildPath(template, {
		...path,
		...query
	}));
	const request = {
		method: route.def.method,
		path: template,
		url: url.href,
		headers: {
			...body === void 0 ? {} : { "content-type": "application/json" },
			...options.headers,
			...headers,
			...callOptions?.headers
		},
		...body === void 0 ? {} : { body }
	};
	let response;
	let text;
	try {
		response = await fetch(url, {
			method: request.method,
			headers: request.headers,
			body: body === void 0 ? void 0 : JSON.stringify(body),
			...route.def.session === false ? {} : { credentials: "include" }
		});
		text = response.status === 204 ? "" : await response.text();
	} catch (error) {
		report(options, {
			kind: "network",
			request,
			error
		});
		throw error;
	}
	const status = response.status;
	const responseHeaders = Object.fromEntries(response.headers);
	const schema = route.def.outputs[status];
	if (!schema) {
		const undeclared = jsonOrText(text);
		return fail(options, request, {
			status,
			headers: responseHeaders,
			body: undeclared
		}, new ContractResponseError(`Unexpected status ${status} for ${route.def.method} ${template}`, status, undeclared));
	}
	let raw;
	try {
		raw = text === "" ? void 0 : JSON.parse(text);
	} catch (cause) {
		return fail(options, request, {
			status,
			headers: responseHeaders,
			body: text
		}, new ContractResponseError(`Invalid JSON response body for ${route.def.method} ${template} (${status})`, status, text, { cause }));
	}
	const parsed = schema.safeParse(raw);
	if (!parsed.success) {
		const response = {
			status,
			headers: responseHeaders,
			body: raw
		};
		const error = new ContractResponseError(`Invalid response body for ${route.def.method} ${template} (${status}):\n${z.prettifyError(parsed.error)}`, status, raw, { cause: parsed.error });
		if (options.strict === false) {
			report(options, {
				kind: "response",
				request,
				response,
				error
			});
			return {
				status,
				body: raw,
				request,
				response
			};
		}
		return fail(options, request, response, error);
	}
	return {
		status,
		body: parsed.data,
		request,
		response: {
			status,
			headers: responseHeaders,
			body: parsed.data
		}
	};
}
function jsonOrText(text) {
	if (text === "") return text;
	try {
		return JSON.parse(text);
	} catch {
		return text;
	}
}
function fail(options, request, response, error) {
	report(options, {
		kind: "response",
		request,
		response,
		error
	});
	throw error;
}
function setContractClientErrorHandler(handler) {
	globalThis.__sparkContractClientErrorHandler = handler;
}
function warnContractResponseError(event) {
	if (event.kind !== "response") return;
	console.warn(`ContractResponseError: ${event.error.message}\nURL: ${event.request.url}`);
}
function report(options, event) {
	const handler = options.onError ?? globalThis.__sparkContractClientErrorHandler ?? warnContractResponseError;
	try {
		handler(event);
	} catch {
		return;
	}
}
/**
* The array field holding the items of a paginated route, read from its lowest
* 2xx response schema (all 2xx schemas of a paginated route declare exactly one).
*/
function itemsField(route) {
	const status = Object.keys(route.def.outputs).map(Number).filter((code) => code >= 200 && code < 300).sort((a, b) => a - b)[0];
	let schema = status === void 0 ? void 0 : route.def.outputs[status];
	if (schema instanceof z.ZodPipe) schema = schema.out;
	if (!(schema instanceof z.ZodObject)) throw new Error("Paginated route declares no 2xx object response schema");
	return paginatedArrayField(schema);
}
async function* paginateAll(template, route, options, input, callOptions) {
	const pagination = route.def.pagination;
	if (pagination === false) throw new Error(`Route ${route.def.method} ${template} is not paginated`);
	const field = itemsField(route);
	const resolved = resolveInput(route, input);
	let query = { ...resolved.query };
	while (true) {
		const response = await perform(template, route, options, {
			...resolved,
			query
		}, callOptions);
		if (response.status < 200 || response.status >= 300) fail(options, response.request, response.response, new ContractResponseError(`Unexpected status ${response.status} while paginating ${route.def.method} ${template}`, response.status, response.body));
		const body = response.body;
		const items = body[field];
		yield* items;
		if (pagination.kind === "offset") {
			const next = (body.pagination.offset ?? 0) + items.length;
			if (items.length === 0 || next >= (body.pagination.total ?? 0)) return;
			query = {
				...query,
				offset: next
			};
		} else {
			const cursor = body.pagination.nextCursor;
			if (cursor === null || cursor === void 0 || items.length === 0) return;
			query = {
				...query,
				cursor
			};
		}
	}
}
function resolveInput(route, input) {
	if (input === void 0 || input === null) return {};
	const declared = INPUT_KINDS.filter((kind) => route.def.inputs[kind]);
	if (typeof input !== "object") return declared.length === 1 ? { [declared[0]]: input } : {};
	if (declared.length === 1) {
		const keys = Object.keys(input);
		if (!(keys.length > 0 && keys.every((key) => INPUT_KINDS.includes(key)) && keys.some((key) => declared.includes(key)))) return { [declared[0]]: input };
	}
	return input;
}

//#endregion
//#region src/contract/openapi.ts
const SESSION_SCHEME = "session";
const BEARER_SCHEME = "bearer";
const ErrorResponseSchema = z.object({
	code: z.string(),
	message: z.string()
});
/**
* Generate an OpenAPI document for a contract.
*
* Routes that declare inputs get an implicit `400` validation-error response unless they
* declare an explicit `400` output. Routes that declare a session requirement reference the
* `session` cookie security scheme and get implicit `401` (and `403` for kind-restricted
* routes) responses; routes that declare a bearer requirement reference the `bearer` http
* security scheme likewise. Routes that require features list them in the description and
* an `x-required-features` extension, and get an implicit `404` feature-disabled response.
* Paginated routes describe their pagination in the description and an `x-pagination`
* extension; the pagination query params and response `pagination` object are part of
* the route schemas themselves.
*/
function generateOpenApiSpec(contract, options = {}) {
	const openapi = options.openapi ?? "3.1.0";
	const target = openapi === "3.0.0" ? "openapi-3.0" : "draft-2020-12";
	let hasSessionRoutes = false;
	let hasBearerRoutes = false;
	const paths = {};
	for (const [routerId, router] of Object.entries(contract.def.routers)) for (const [routeId, route] of Object.entries(router.def.routes)) {
		hasSessionRoutes ||= route.def.session !== false;
		hasBearerRoutes ||= route.def.bearer !== false;
		const path = router.def.prefix + route.def.path;
		paths[path] = {
			...paths[path],
			[route.def.method.toLowerCase()]: operation(`${routerId}.${routeId}`, route, target)
		};
	}
	return {
		openapi,
		info: {
			title: options.title ?? contract.def.title ?? "API",
			version: options.version ?? "1.0.0",
			...options.description ? { description: options.description } : {}
		},
		paths,
		...hasSessionRoutes || hasBearerRoutes ? { components: { securitySchemes: {
			...hasSessionRoutes ? { [SESSION_SCHEME]: {
				type: "apiKey",
				in: "cookie",
				name: options.sessionCookieName ?? "token",
				description: "Spark session cookie."
			} } : {},
			...hasBearerRoutes ? { [BEARER_SCHEME]: {
				type: "http",
				scheme: "bearer",
				description: "Spark API key token."
			} } : {}
		} } } : {}
	};
}
function operation(operationId, route, target) {
	const { query, path, headers, body } = route.def.inputs;
	const session = route.def.session;
	const bearer = route.def.bearer;
	const features = route.def.features;
	const pagination = route.def.pagination;
	const parameters = [
		...parametersFor(path, "path", target),
		...parametersFor(query, "query", target),
		...parametersFor(headers, "header", target)
	];
	const responses = {};
	for (const [status, schema] of Object.entries(route.def.outputs)) responses[status] = status === "204" ? { description: `${status} response` } : {
		description: `${status} response`,
		content: { "application/json": { schema: toJsonSchema(schema, target, "output") } }
	};
	if (Object.keys(route.def.inputs).length > 0 && !responses["400"]) responses["400"] = errorResponse("Input validation failed", target);
	if (session !== false) {
		responses["401"] ??= errorResponse("No active session", target);
		if (session !== true) responses["403"] ??= errorResponse("Session does not have the required user kind", target);
	}
	if (bearer !== false) {
		responses["401"] ??= errorResponse("No valid bearer API key", target);
		if (bearer !== true) responses["403"] ??= errorResponse("API key does not have the required prefix", target);
	}
	if (features.length > 0) responses["404"] ??= errorResponse("Endpoint unavailable: a required feature is disabled", target);
	const sessionNote = session === false ? null : session === true ? "Requires a session." : `Requires a ${session.join(" or ")} session.`;
	const bearerNote = bearer === false ? null : bearer === true ? "Requires a bearer API key." : `Requires a bearer API key with the ${bearer} prefix.`;
	const featureNote = features.length === 0 ? null : features.length === 1 ? `Requires the ${features[0]} feature to be enabled.` : `Requires the ${features.join(" and ")} features to be enabled.`;
	const paginationNote = pagination === false ? null : `Paginated with ${pagination.kind} pagination (limit default ${pagination.limit.default}, max ${pagination.limit.max}).`;
	const description = [
		route.def.meta.description,
		sessionNote,
		bearerNote,
		featureNote,
		paginationNote
	].filter(Boolean).join(" ");
	return {
		operationId,
		...description ? { description } : {},
		...parameters.length > 0 ? { parameters } : {},
		...body ? { requestBody: {
			required: true,
			content: { "application/json": { schema: toJsonSchema(body, target, "input") } }
		} } : {},
		responses,
		...session === false && bearer === false ? {} : { security: [{
			...session === false ? {} : { [SESSION_SCHEME]: [] },
			...bearer === false ? {} : { [BEARER_SCHEME]: [] }
		}] },
		...features.length > 0 ? { "x-required-features": features } : {},
		...pagination === false ? {} : { "x-pagination": pagination }
	};
}
function errorResponse(description, target) {
	return {
		description,
		content: { "application/json": { schema: toJsonSchema(ErrorResponseSchema, target, "output") } }
	};
}
function parametersFor(schema, location, target) {
	if (!schema) return [];
	const json = toJsonSchema(schema, target, "input");
	return Object.entries(json.properties ?? {}).map(([name, property]) => ({
		name,
		in: location,
		required: location === "path" || (json.required?.includes(name) ?? false),
		schema: property
	}));
}
function toJsonSchema(schema, target, io) {
	const { $schema, ...json } = z.toJSONSchema(schema, {
		target,
		io,
		unrepresentable: "any"
	});
	return json;
}

//#endregion
//#region src/contract/index.ts
/**
* Entry point for defining API contracts:
* `api.route({ method, path })`, `api.router()`, and `api.contract({ title })`.
*/
const api = {
	route,
	router,
	contract
};

//#endregion
//#region src/session/kind-check.ts
/**
* @param session session to check
* @returns `true` if this session belongs to a teacher.
*/
function isTeacher(session) {
	if ("user" in session) return "teacher" in session.user;
	return "teacher" in session;
}
/**
* @param session session to check
* @returns `true` if this session belongs to a student.
*/
function isStudent(session) {
	if ("user" in session) return "student" in session.user;
	return "student" in session;
}

//#endregion
//#region src/session/legal/consent.ts
/**
* @returns All possible permissions in the application.
*/
function permissions() {
	return PERMISSIONS;
}
/**
* Default permissions mapping matching permissions that are always enabled.
*/
const DefaultConsent = deepFreeze({
	functional: true,
	"limited-analytics": true,
	analytics: false,
	marketing: false
});
/**
* Default accepted terms mapping matching terms that are always accepted.
*
* Currently empty as there are no terms that are always accepted,
* but this can be updated in the future as needed.
*/
const DefaultAcceptedTerms = deepFreeze({});

//#endregion
//#region src/session/legal/terms.ts
/**
* @returns All terms and policies for Spark.
*/
function terms() {
	return TERMS;
}
/**
* @param term Term or policy.
* @returns The latest version.
*/
function getTermVersion(term) {
	const versions = TERM_VERSIONS[term];
	if (!versions) throw new Error(`No versions found for term: ${term}`);
	return versions[versions.length - 1].version;
}
/**
* @param term Term or policy.
* @returns All versions.
*/
function getAllTermVersions(term) {
	if (!TERM_VERSIONS[term]) throw new Error(`No versions found for term: ${term}`);
	return TERM_VERSIONS[term].map((entry) => entry.version);
}

//#endregion
//#region src/session/jwt/base64.ts
function decodeBase64Url(input) {
	const base64 = input.replace(/-/g, "+").replace(/_/g, "/");
	return decodeBase64(base64.padEnd(base64.length + (4 - base64.length % 4) % 4, "="));
}
function decodeBase64(input) {
	const base64 = input.replace(/-/g, "+").replace(/_/g, "/");
	const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, "=");
	try {
		if (typeof Uint8Array.fromBase64 === "function") return ok(Uint8Array.fromBase64(padded));
		else if (typeof Buffer !== "undefined") return ok(Uint8Array.from(Buffer.from(padded, "base64")));
		return ok(decodeBase64ToBytesFallback(padded));
	} catch {
		return err("invalid_format");
	}
}
function decodeBase64ToBytesFallback(base64) {
	const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
	const lookup = /* @__PURE__ */ new Uint8Array(256);
	lookup.fill(255);
	for (let i = 0; i < 64; i++) lookup[alphabet.charCodeAt(i)] = i;
	const clean = base64.replace(/=+$/, "");
	const outputLength = Math.floor(clean.length * 3 / 4);
	const bytes = new Uint8Array(outputLength);
	let buffer = 0;
	let bits = 0;
	let offset = 0;
	for (let i = 0; i < clean.length; i++) {
		const value = lookup[clean.charCodeAt(i)];
		if (value === 255) throw new Error("Invalid base64 string");
		buffer = buffer << 6 | value;
		bits += 6;
		if (bits >= 8) {
			bits -= 8;
			bytes[offset++] = buffer >> bits & 255;
		}
	}
	return bytes;
}

//#endregion
//#region src/session/jwt/parse.ts
const HeaderSchema = z.object({
	typ: z.literal("JWT"),
	alg: z.enum(["RS256"]),
	kid: z.string().optional()
});
/**
* Decode a JWT token without verifying the signature.
*
* @param jwt JWT token string.
* @returns Decoded JWT with typed claims.
*/
function parseJWT(jwt) {
	return splitHeader(jwt).and(({ header, payload, signature }) => {
		const data = new TextEncoder().encode(`${header}.${payload}`);
		return ok({
			header,
			payload,
			signature,
			data
		});
	}).and(decodeHeader).and(parseClaims).and(decodeSignature);
}
function splitHeader(jwt) {
	const parts = jwt.split(".");
	if (parts.length !== 3) return err("invalid_format");
	const [header, payload, signature] = parts;
	return ok({
		header,
		payload,
		signature
	});
}
function decodeHeader(data) {
	return parseBase64UrlJson(data.header, HeaderSchema).map((header) => ({
		...data,
		header
	})).mapErr(() => "invalid_header");
}
function parseClaims(data) {
	return parseBase64UrlJson(data.payload, z.record(z.string(), z.unknown())).map((claims) => ({
		...data,
		claims
	})).mapErr(() => "invalid_claims");
}
function decodeSignature(data) {
	return decodeBase64Url(data.signature).map((signature) => ({
		...data,
		signature
	}));
}
function parseBase64UrlJson(input, schema) {
	try {
		return ok(schema.parse(JSON.parse(new TextDecoder().decode(decodeBase64Url(input).unwrap()))));
	} catch {
		return err("invalid_format");
	}
}

//#endregion
//#region src/session/jwt/decode.ts
function decodeJWT(jwt, claimsSchema) {
	if (!claimsSchema) return parseJWT(jwt).map(({ header }) => ({
		header,
		claims: void 0
	}));
	return parseJWT(jwt).and(({ header, claims }) => {
		const validation = claimsSchema.safeParse(claims);
		if (!validation.success) return err("invalid_claims");
		return ok({
			header,
			claims: validation.data
		});
	});
}

//#endregion
//#region src/session/jwt/verify.ts
async function verifyJWT(jwt, publicKey, claimsSchema) {
	const { header, claims, data, signature } = parseJWT(jwt).unwrap();
	if (!await crypto.subtle.verify("RSASSA-PKCS1-v1_5", publicKey, signature, data)) throw new Error("Invalid JWT signature");
	if ("exp" in claims && typeof claims.exp === "number") {
		const now = Math.floor(Date.now() / 1e3);
		if (claims.exp < now) throw new Error("JWT has expired");
	}
	if ("nbf" in claims && typeof claims.nbf === "number") {
		const now = Math.floor(Date.now() / 1e3);
		if (claims.nbf > now) throw new Error("JWT is not valid yet");
	}
	if ("iat" in claims && typeof claims.iat === "number") {
		const now = Math.floor(Date.now() / 1e3);
		if (claims.iat > now) throw new Error("JWT was issued in the future");
	}
	if (claimsSchema) return {
		header,
		claims: claimsSchema.parse(claims)
	};
	return {
		header,
		claims: void 0
	};
}
async function importPublicKey(publicKey) {
	return await crypto.subtle.importKey("spki", typeof publicKey === "string" ? parsePemPublicKey(publicKey) : publicKey, {
		name: "RSASSA-PKCS1-v1_5",
		hash: "SHA-256"
	}, false, ["verify"]);
}
function parsePemPublicKey(pem) {
	const pemBody = pem.replace(/-----BEGIN PUBLIC KEY-----/, "").replace(/-----END PUBLIC KEY-----/, "").replace(/\s+/g, "");
	return decodeBase64(pemBody).unwrap();
}

//#endregion
//#region src/session/spark-jwt.ts
/**
* Decode a Spark JWT token and validate its claims against the ClaimsSchema.
*
* Important: This function does NOT verify the JWT signature.
* Use `verifySparkJWT` for signature verification.
* @see verifySparkJWT
* @param jwt a Spark JWT token.
* @returns Decoded Spark claims.
* @example
* ```ts
* import { decodeSparkJWT } from "@spark/web";
* const claims = decodeSparkJWT(jwt).unwrap();
* ```
*/
function decodeSparkJWT(jwt) {
	return decodeJWT(jwt, ClaimsSchema).map(({ claims }) => claims);
}
/**
* Decode and verify a Spark JWT token using the provided public key.
*
* This function validates both the JWT signature and the claims against the ClaimsSchema.
*
* Import publics keys using:
* @param jwt a Spark JWT token.
* @param publicKey public key to verify the JWT signature.
* @returns Decoded Spark claims.
* @example
* ```ts
* import { importPublicKey, verifySparkJWT } from "@spark/web";
* const publicKey = await importPublicKey(pemString);
* const claims = await verifySparkJWT(jwt, publicKey);
* ```
*/
async function verifySparkJWT(jwt, publicKey) {
	const { claims } = await verifyJWT(jwt, publicKey, ClaimsSchema);
	return claims;
}

//#endregion
//#region src/session/user.ts
/**
* @param claims Session claims.
* @returns user kind based on session claims.
*/
function getUserKindFromClaims(claims) {
	if ("teacher" in claims && claims.teacher) return "teacher";
	if ("student" in claims && claims.student) return "student";
	return "internal";
}

//#endregion
export { ContractClient, ContractResponseError, DefaultAcceptedTerms, DefaultConsent, api, decodeSparkJWT, generateOpenApiSpec, getAllTermVersions, getTermVersion, getUserKindFromClaims, importPublicKey, isStudent, isTeacher, metaExamples, permissions, setContractClientErrorHandler, terms, verifySparkJWT };